import React, { useState } from 'react';

/**
 * KpiEditorCard — inline "add a KPI" row on the employee magic-link form.
 *
 * KPIs have no system source of truth (most dynamic part of the company),
 * so the employee types them per the canonical EPR Form: KPI Name +
 * Target. Actual / Achievement % / score are filled on the item card the
 * row becomes after adding. Presentational — the page owns the write.
 */

export interface KpiEditorCardProps {
  disabled?: boolean;
  /** Resolves when the row is persisted; the form clears on success. */
  onAddKpi: (name: string, target?: string) => Promise<void>;
}

const KpiEditorCard: React.FC<KpiEditorCardProps> = ({ disabled = false, onAddKpi }) => {
  const [name, setName]       = useState('');
  const [target, setTarget]   = useState('');
  const [adding, setAdding]   = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const canAdd = !disabled && !adding && name.trim().length > 0;

  const handleAdd = async () => {
    if (!canAdd) return;
    setAdding(true);
    setError(null);
    try {
      await onAddKpi(name.trim(), target.trim() || undefined);
      setName('');
      setTarget('');
    } catch (e) {
      setError((e as Error).message || 'Could not add the KPI.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50/50 p-4">
      <div className="text-sm font-semibold text-stone-900 mb-2">Add a KPI</div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_minmax(0,180px)_auto] gap-2 items-end">
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor="kpi-add-name">
            KPI name
          </label>
          <input
            id="kpi-add-name"
            type="text"
            value={name}
            disabled={disabled || adding}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void handleAdd(); }}
            placeholder="e.g. Monthly qualified leads"
            className={[
              'w-full rounded-lg border bg-white px-3 py-2 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-stone-400',
              'disabled:bg-stone-50 disabled:text-stone-600',
              'border-stone-300',
            ].join(' ')}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor="kpi-add-target">
            Target <span className="font-normal text-stone-400">(optional)</span>
          </label>
          <input
            id="kpi-add-target"
            type="text"
            value={target}
            disabled={disabled || adding}
            onChange={e => setTarget(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void handleAdd(); }}
            placeholder="e.g. 100"
            className={[
              'w-full rounded-lg border bg-white px-3 py-2 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-stone-400',
              'disabled:bg-stone-50 disabled:text-stone-600',
              'border-stone-300',
            ].join(' ')}
          />
        </div>
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={!canAdd}
          className={[
            'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium',
            'bg-stone-900 text-white hover:bg-stone-800',
            'disabled:bg-stone-300 disabled:cursor-not-allowed',
          ].join(' ')}
        >
          <span className="material-symbols-rounded" style={{ fontSize: 18 }} aria-hidden="true">
            {adding ? 'progress_activity' : 'add'}
          </span>
          Add
        </button>
      </div>
      {error && (
        <div className="mt-2 text-sm text-red-700" role="alert">{error}</div>
      )}
    </div>
  );
};

export default KpiEditorCard;
